import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Gift, Smartphone, Wifi, Loader2, CheckCircle, XCircle, X, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PromotionalBalance = () => {
  const { user, token } = useAuth();
  const [promotionalStatus, setPromotionalStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('paquetes'); // 'paquetes' o 'recargas'
  const [planCategory, setPlanCategory] = useState('libre'); // 'libre' o 'libre_plus'
  const [processing, setProcessing] = useState(false);
  const [plans, setPlans] = useState({});
  
  // Form states
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [transactionResult, setTransactionResult] = useState(null);
  const [servicesConfig, setServicesConfig] = useState({ topups: true, packages: true });

  useEffect(() => {
    loadPromotionalStatus();
  }, []);

  const loadPromotionalStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/promotional/user-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setPromotionalStatus(data);
      
      if (data.promotional_balance_enabled) {
        const plansResponse = await fetch(`${API_URL}/api/promotional/plans`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const plansData = await plansResponse.json();
        setPlans(plansData.plans || {});
        
        // Load config to check which services are enabled
        const configResponse = await fetch(`${API_URL}/api/promotional/config`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const configData = await configResponse.json();
        setServicesConfig({
          topups: configData.promotional_topups_enabled !== false,
          packages: configData.promotional_packages_enabled !== false
        });
      }
    } catch (error) {
      console.error('Error loading promotional status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendDataPackage = async () => {
    if (!phoneNumber || !selectedPlan) {
      toast.error('Por favor ingresa el número y selecciona un plan');
      return;
    }

    setProcessing(true);
    setTransactionResult(null);

    try {
      const response = await fetch(`${API_URL}/api/promotional/data-package`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone_number: phoneNumber,
          plan: selectedPlan
        })
      });

      const data = await response.json();

      if (response.ok) {
        setTransactionResult({
          success: true,
          message: 'Paquete enviado exitosamente',
          details: {
            ...data,
            phone_number: phoneNumber,
            plan_name: data.plan || plans[selectedPlan]?.name
          }
        });
        toast.success('Paquete enviado exitosamente');
        loadPromotionalStatus(); // Refresh balance
        setPhoneNumber('');
        setSelectedPlan(null);
      } else {
        // Interceptar mensaje de Midas sobre balance y mostrar mensaje genérico
        let errorMessage = data.detail || 'Error al enviar paquete';
        if (errorMessage.toLowerCase().includes('balance') || 
            errorMessage.toLowerCase().includes('seleccione otro') ||
            errorMessage.toLowerCase().includes('gracias por utilizar')) {
          errorMessage = 'No disponible, trate más tarde';
        }
        setTransactionResult({
          success: false,
          message: errorMessage
        });
        toast.error(errorMessage);
      }
    } catch (error) {
      setTransactionResult({
        success: false,
        message: 'Error de conexión'
      });
      toast.error('Error de conexión');
    } finally {
      setProcessing(false);
    }
  };

  const handleSendTopup = async () => {
    if (!phoneNumber || !rechargeAmount) {
      toast.error('Por favor ingresa el número y el monto');
      return;
    }

    const amount = parseFloat(rechargeAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Monto inválido');
      return;
    }

    setProcessing(true);
    setTransactionResult(null);

    try {
      const response = await fetch(`${API_URL}/api/promotional/topup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone_number: phoneNumber,
          amount: amount
        })
      });

      const data = await response.json();

      if (response.ok) {
        setTransactionResult({
          success: true,
          message: 'Recarga enviada exitosamente',
          details: {
            ...data,
            phone_number: phoneNumber,
            plan: 'Recarga Promocional',
            amount: data.amount || amount
          }
        });
        toast.success('Recarga enviada exitosamente');
        loadPromotionalStatus(); // Refresh balance
        setPhoneNumber('');
        setRechargeAmount('');
      } else {
        // Interceptar mensaje de Midas sobre balance y mostrar mensaje genérico
        let errorMessage = data.detail || 'Error al enviar recarga';
        if (errorMessage.toLowerCase().includes('balance') || 
            errorMessage.toLowerCase().includes('seleccione otro') ||
            errorMessage.toLowerCase().includes('gracias por utilizar')) {
          errorMessage = 'No disponible, trate más tarde';
        }
        setTransactionResult({
          success: false,
          message: errorMessage
        });
        toast.error(errorMessage);
      }
    } catch (error) {
      setTransactionResult({
        success: false,
        message: 'Error de conexión'
      });
      toast.error('Error de conexión');
    } finally {
      setProcessing(false);
    }
  };

  // Don't render if not enabled
  if (loading) return null;
  if (!promotionalStatus?.promotional_balance_enabled) return null;

  return (
    <>
      {/* Promotional Balance Card - Next to regular balance */}
      <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0 shadow-lg" data-testid="promotional-balance-card">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="bg-white/20 backdrop-blur-sm p-2 rounded-xl flex-shrink-0">
                <Gift className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-emerald-100">Balance Promocional</p>
                <h3 className="text-lg sm:text-xl font-bold truncate">
                  RD$ {(promotionalStatus?.promotional_balance || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                </h3>
              </div>
            </div>
            <Button 
              onClick={() => setShowModal(true)}
              className="bg-white text-emerald-600 hover:bg-emerald-50 flex-shrink-0 text-sm sm:text-base px-3 sm:px-4"
              data-testid="use-promotional-balance-btn"
            >
              Usar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Modal for using promotional balance */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="w-[95vw] sm:max-w-[480px] max-h-[95vh] overflow-hidden rounded-lg p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Gift className="w-4 h-4 text-emerald-500" />
              Balance Promocional
            </DialogTitle>
            <DialogDescription className="text-sm">
              Disponible: <span className="font-bold text-emerald-600">RD$ {(promotionalStatus?.promotional_balance || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</span>
            </DialogDescription>
          </DialogHeader>

          {/* Tabs principales */}
          <div className="flex gap-2 mb-3">
            {servicesConfig.packages && (
              <Button
                variant={activeTab === 'paquetes' ? 'default' : 'outline'}
                onClick={() => { setActiveTab('paquetes'); setTransactionResult(null); }}
                className="flex-1 text-sm h-9"
                size="sm"
              >
                <Wifi className="w-4 h-4 mr-1" />
                Paquetes
              </Button>
            )}
            {servicesConfig.topups && (
              <Button
                variant={activeTab === 'recargas' ? 'default' : 'outline'}
                onClick={() => { setActiveTab('recargas'); setTransactionResult(null); }}
                className="flex-1 text-sm h-9"
                size="sm"
              >
                <Smartphone className="w-4 h-4 mr-1" />
                Recargas
              </Button>
            )}
          </div>

          {/* No services message */}
          {!servicesConfig.packages && !servicesConfig.topups && (
            <div className="text-center py-4 text-gray-500">
              <p>No hay servicios promocionales disponibles</p>
            </div>
          )}

          {/* Transaction Result - Comprobante completo al éxito */}
          {transactionResult && (
            transactionResult.success ? (
              <div className="mb-3 rounded-lg border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden" data-testid="promotional-receipt">
                <div className="bg-emerald-500 text-white px-3 py-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-sm leading-tight">
                      {transactionResult.details?.ussd_response || transactionResult.message}
                    </p>
                  </div>
                </div>
                <div className="p-3 text-xs space-y-1.5">
                  {transactionResult.details?.reference && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Referencia:</span>
                      <span className="font-mono font-semibold text-gray-800">{transactionResult.details.reference}</span>
                    </div>
                  )}
                  {transactionResult.details?.plan && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Plan:</span>
                      <span className="font-semibold text-gray-800">{transactionResult.details.plan}</span>
                    </div>
                  )}
                  {transactionResult.details?.phone_number && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Número:</span>
                      <span className="font-semibold text-gray-800">{transactionResult.details.phone_number}</span>
                    </div>
                  )}
                  {transactionResult.details?.amount != null && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monto:</span>
                      <span className="font-semibold text-gray-800">RD$ {parseFloat(transactionResult.details.amount).toFixed(2)}</span>
                    </div>
                  )}
                  {transactionResult.details?.commission != null && (
                    <div className="flex justify-between text-emerald-700">
                      <span>Tu ganancia:</span>
                      <span className="font-bold">+ RD$ {parseFloat(transactionResult.details.commission).toFixed(2)}</span>
                    </div>
                  )}
                  {transactionResult.details?.new_promotional_balance != null && (
                    <div className="flex justify-between pt-1.5 mt-1 border-t border-emerald-200">
                      <span className="text-gray-600">Balance Restante:</span>
                      <span className="font-bold text-emerald-700">RD$ {parseFloat(transactionResult.details.new_promotional_balance).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="pt-1.5 text-[10px] text-gray-500 text-center">
                    {new Date().toLocaleString('es-DO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-lg mb-3 bg-red-50 border border-red-200">
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-700">{transactionResult.message}</span>
                </div>
              </div>
            )
          )}

          {/* Phone Number Input - Más compacto */}
          <div className="space-y-1 mb-3">
            <Label htmlFor="phone" className="text-sm">Número Claro</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="8091234567"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
              maxLength={10}
              className="h-9"
              data-testid="promotional-phone-input"
            />
          </div>

          {activeTab === 'paquetes' ? (
            <>
              {/* Tabs para categorías de planes */}
              <Tabs value={planCategory} onValueChange={(v) => { setPlanCategory(v); setSelectedPlan(null); }} className="mb-3">
                <TabsList className="grid w-full grid-cols-2 mb-2 h-9">
                  <TabsTrigger 
                    value="libre" 
                    className="text-sm data-[state=active]:bg-emerald-500 data-[state=active]:text-white"
                  >
                    <Wifi className="w-3 h-3 mr-1" />
                    Libre
                  </TabsTrigger>
                  <TabsTrigger 
                    value="libre_plus"
                    className="text-sm data-[state=active]:bg-blue-500 data-[state=active]:text-white"
                  >
                    <Zap className="w-3 h-3 mr-1" />
                    Libre Plus
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="libre" className="mt-0">
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(plans)
                      .filter(([key, plan]) => plan.category === 'libre' || !plan.category)
                      .map(([key, plan]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedPlan(key)}
                        className={`p-2 rounded-lg border-2 text-center transition-all ${
                          selectedPlan === key 
                            ? 'border-emerald-500 bg-emerald-50 shadow-sm' 
                            : 'border-gray-200 hover:border-emerald-300 bg-white'
                        }`}
                        data-testid={`plan-${key}`}
                      >
                        <p className="font-medium text-xs text-slate-600">{plan.name.replace(' Libre', '')}</p>
                        <p className="text-emerald-600 font-bold text-sm">RD$ {plan.price}</p>
                      </button>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="libre_plus" className="mt-0">
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(plans)
                      .filter(([key, plan]) => plan.category === 'libre_plus')
                      .map(([key, plan]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedPlan(key)}
                        className={`p-2 rounded-lg border-2 text-center transition-all ${
                          selectedPlan === key 
                            ? 'border-blue-500 bg-blue-50 shadow-sm' 
                            : 'border-gray-200 hover:border-blue-300 bg-white'
                        }`}
                        data-testid={`plan-${key}`}
                      >
                        <p className="font-medium text-xs text-slate-600">{plan.name.replace(' Libre Plus', '')}</p>
                        <p className="text-blue-600 font-bold text-sm">RD$ {plan.price}</p>
                      </button>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>

              <Button
                onClick={handleSendDataPackage}
                disabled={processing || !phoneNumber || !selectedPlan}
                className={`w-full h-10 ${planCategory === 'libre_plus' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                data-testid="send-data-package-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    {planCategory === 'libre_plus' ? <Zap className="w-4 h-4 mr-2" /> : <Wifi className="w-4 h-4 mr-2" />}
                    Enviar Paquete
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              {/* Recharge Amount */}
              <div className="space-y-1 mb-3">
                <Label htmlFor="amount" className="text-sm">Monto de recarga</Label>
                <Input
                  id="amount"
                  type="number"
                  placeholder="100"
                  value={rechargeAmount}
                  onChange={(e) => setRechargeAmount(e.target.value)}
                  min="1"
                  className="h-9"
                  data-testid="promotional-amount-input"
                />
              </div>

              <Button
                onClick={handleSendTopup}
                disabled={processing || !phoneNumber || !rechargeAmount}
                className="w-full h-10 bg-emerald-600 hover:bg-emerald-700"
                data-testid="send-topup-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Smartphone className="w-4 h-4 mr-2" />
                    Enviar Recarga
                  </>
                )}
              </Button>
            </>
          )}

          {/* Close Button */}
          <Button
            variant="outline"
            onClick={() => setShowModal(false)}
            className="w-full mt-2 h-9"
            data-testid="close-promotional-modal-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Cerrar
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PromotionalBalance;
